/**
 * I3: Feature Flag Context
 * 
 * Provides feature flag state to the entire app.
 * - Flags fetched once at login via GET /api/feature-flags/user
 * - Cached in context until logout
 * - Admins bypass all flags (see all features regardless of flag state)
 * 
 * Flag keys match backend DEFAULT_FLAGS_V2:
 * - messaging, peer_reviews, referral_program, ats_integration, team_comparison, pipeline
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const FeatureFlagContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FeatureFlagProvider = ({ children }) => {
  // Flags state: { [key]: boolean }
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { user, getToken } = useAuth();

  /**
   * Fetch flags from backend for current user.
   * Called automatically when user changes (login/logout).
   */
  const fetchFlags = useCallback(async () => {
    const token = getToken();
    
    if (!token || !user) {
      // No user logged in - reset flags
      setFlags({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_URL}/feature-flags/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Response is { flag_key: boolean, ... }
      setFlags(response.data || {});
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      setError(err.message || 'Failed to load feature flags');
      // Fail-closed: if we can't fetch flags, assume all flags are disabled
      setFlags({});
    } finally {
      setLoading(false);
    }
  }, [user, getToken]);

  // Fetch flags when user changes (login/logout)
  useEffect(() => {
    fetchFlags();
  }, [user?.id, fetchFlags]);

  /**
   * Check if a specific feature flag is enabled.
   * 
   * Admins always return true (bypass all flags).
   * Non-admins: return the flag's enabled state, defaulting to false if not found.
   * 
   * @param {string} key - Feature flag key (e.g., 'messaging', 'peer_reviews')
   * @returns {boolean} - True if feature is enabled for this user
   */
  const isFeatureEnabled = useCallback((key) => {
    // Admin bypass: admins see all features regardless of flag state
    if (user?.is_admin || user?.is_superadmin) {
      return true;
    }
    
    // For non-admins, check the flag state
    // Default to false if flag not found (fail-closed)
    return flags[key] === true;
  }, [flags, user]);

  /**
   * Get all flags (for debugging or admin display)
   */
  const getAllFlags = useCallback(() => {
    return { ...flags };
  }, [flags]);

  /**
   * Refresh flags (e.g., after admin toggles a flag)
   */
  const refreshFlags = useCallback(() => {
    return fetchFlags();
  }, [fetchFlags]);

  return (
    <FeatureFlagContext.Provider value={{
      flags,
      loading,
      error,
      isFeatureEnabled,
      getAllFlags,
      refreshFlags,
    }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to access feature flag context.
 * 
 * Usage:
 *   const { isFeatureEnabled } = useFeatureFlags();
 *   if (isFeatureEnabled('messaging')) { ... }
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

/**
 * Convenience hook to check a single feature flag.
 * 
 * Usage:
 *   const messagingEnabled = useFeatureFlag('messaging');
 *   if (messagingEnabled) { ... }
 * 
 * @param {string} key - Feature flag key
 * @returns {boolean} - True if feature is enabled for current user
 */
export const useFeatureFlag = (key) => {
  const { isFeatureEnabled, loading } = useFeatureFlags();
  
  // While loading, return false (fail-closed)
  if (loading) {
    return false;
  }
  
  return isFeatureEnabled(key);
};
